import React, { useState, useEffect } from 'react'
import { Button, Col, Form, Row, Card } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { getDepartmentsForSelect } from '../../actions/departmentsActions'

const getDepartmentValue = (department) => {
    if (typeof department === 'string') {
        return department
    }

    return department?.code || department?.name || department?._id || ''
}

const normalizeDepartments = (departments = []) => {
    return [...new Set(
        departments
            .map(getDepartmentValue)
            .map(department => String(department || '').trim().toUpperCase())
            .filter(Boolean)
    )]
}

const getDepartmentOptions = (departments = [], extraDepartments = []) => {
    const options = departments
        .map(department => {
            const value = getDepartmentValue(department)

            return {
                value: String(value || '').trim().toUpperCase(),
                label: department?.name || value
            }
        })
        .filter(option => option.value)

    extraDepartments.forEach(department => {
        const value = String(department || '').trim().toUpperCase()

        if (value && !options.some(option => option.value === value)) {
            options.push({
                value,
                label: value
            })
        }
    })

    return options
}

const normalizeAttributes = (attributes, defaultDepartment) => {
    const attributeList = Array.isArray(attributes)
        ? attributes
        : Object.entries(attributes || {}).map(([key, value]) => ({
            key,
            value,
            department: defaultDepartment
        }))

    return attributeList.map((attribute, index) => ({
        id: attribute._id || attribute.id || `${index}-${attribute.key || Date.now()}`,
        _id: attribute._id,
        key: attribute.key || '',
        label: attribute.label || '',
        value: attribute.value ?? '',
        unit: attribute.unit || '',
        department: String(attribute.department || defaultDepartment || '').trim().toUpperCase(),
        source: attribute.source || 'manual',
        confidence: attribute.confidence,
        editableByAll: attribute.editableByAll === true
    }))
}

export default function ProductForm({ state, handleInputChange, handleSubmit, isRegistering, onClose, currentUser }) {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { departments } = useSelector(state => state.departments)
    const hasDepartmentClaim = Array.isArray(currentUser?.departments)
    const userDepartments = normalizeDepartments(
        hasDepartmentClaim ? currentUser.departments : []
    )
    const productDepartments = normalizeDepartments(state.departments || [])
    const defaultDepartment = userDepartments[0] || productDepartments[0] || ''

    const [attributes, setAttributes] = useState(
        normalizeAttributes(state.attributes || [], defaultDepartment)
    )
    const [selectedDepartments, setSelectedDepartments] = useState(
        productDepartments.length ? productDepartments : userDepartments.slice(0, 1)
    )

    const departmentOptions = getDepartmentOptions(departments, [
        ...userDepartments,
        ...selectedDepartments,
    ])
    const isAdmin = currentUser?.rol === 'admin'
    const editableDepartmentOptions = isAdmin
        ? departmentOptions
        : hasDepartmentClaim
            ? departmentOptions.filter(option => userDepartments.includes(option.value))
            : departmentOptions

    useEffect(() => {
        dispatch(getDepartmentsForSelect())
    }, [dispatch])

    useEffect(() => {
        const nextProductDepartments = normalizeDepartments(state.departments || [])
        const nextUserDepartments = normalizeDepartments(currentUser?.departments || [])
        const nextDefaultDepartment = nextUserDepartments[0] || nextProductDepartments[0] || ''

        setSelectedDepartments(
            nextProductDepartments.length
                ? nextProductDepartments
                : nextUserDepartments.slice(0, 1)
        )
        setAttributes(normalizeAttributes(state.attributes || [], nextDefaultDepartment))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state._id])


    const addAttribute = () => {
        setAttributes(prev => [
            ...prev,
            {
                id: Date.now(),
                key: '',
                value: '',
            }
        ])
    }

    const removeAttribute = (id) => {
        setAttributes(prev => prev.filter(attr => attr.id !== id))
    }

    const handleDepartmentChange = (deptId) => {
        setSelectedDepartments(prev =>
            prev.includes(deptId)
                ? prev.filter(id => id !== deptId)
                : [...prev, deptId]
        )
    }

    const handleFormSubmit = (e) => {
        e.preventDefault()
        const productData = {
            ...state,
            reference: state.reference,
            name: state.name,
            description: state.description,
            brand: state.brand,
            group: state.group,
            subgroup: state.subgroup,
            families: state.families,
            departments: selectedDepartments,
            attributes: attributes
                .filter(attr => attr.key)
                .map(attr => ({
                    ...(attr._id ? { _id: attr._id } : {}),
                    key: attr.key,
                    label: attr.label,
                    value: attr.value,
                    unit: attr.unit,
                    source: attr.source,
                    confidence: attr.confidence,
                    editableByAll: attr.editableByAll
                })),
        }
        handleSubmit(productData)
    }

    return (
        <Form onSubmit={handleFormSubmit} className="px-4 formulario">
            <Row>
                <Col xs={12} md={3} className="my-2">
                    <Form.Label>{t('admin.products.reference')}</Form.Label>
                    <Form.Control
                        className="sd-input"
                        type='text'
                        name='reference'
                        value={state.reference || ''}
                        onChange={handleInputChange}
                        required
                    />
                </Col>
                <Col xs={12} md={6} className="my-2">
                    <Form.Label>{t('admin.products.name')}</Form.Label>
                    <Form.Control
                        className="sd-input"
                        type='text'
                        name='name'
                        value={state.name || ''}
                        onChange={handleInputChange}
                    />
                </Col>
                <Col xs={12} md={3} className="my-2">
                    <Form.Label>{t('admin.products.brand')}</Form.Label>
                    <Form.Control
                        className="sd-input"
                        type='text'
                        name='brand'
                        value={state.brand || ''}
                        onChange={handleInputChange}
                    />
                </Col>
                <Col xs={12} md={3} className="my-2">
                    <Form.Label>{t('admin.products.group')}</Form.Label>
                    <Form.Control
                        className="sd-input"
                        type='text'
                        name='group'
                        value={state.group || ''}
                        onChange={handleInputChange}
                    />
                </Col>
                <Col xs={12} md={3} className="my-2">
                    <Form.Label>{t('admin.products.subgroup')}</Form.Label>
                    <Form.Control
                        className="sd-input"
                        type='text'
                        name='subgroup'
                        value={state.subgroup || ''}
                        onChange={handleInputChange}
                    />
                </Col>
            </Row>

            <Row>
                <Col xs={12} className="my-2">
                    <Form.Label>{t('admin.products.description')}</Form.Label>
                    <Form.Control
                        className="sd-input"
                        as='textarea'
                        name='description'
                        value={state.description || ''}
                        onChange={handleInputChange}
                        rows={3}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs={12} className="my-2">
                    <Form.Label>{t('admin.products.families')}</Form.Label>
                    <Form.Control
                        className="sd-input"
                        type='text'
                        name='families'
                        value={Array.isArray(state.families) ? state.families.filter(f => f).join(', ') : state.families || ''}
                        onChange={(e) => {
                            const raw = e.target.value
                            const arr = raw.split(',').map(s => s.trim()).filter(Boolean)
                            handleInputChange({ target: { name: 'families', value: arr } })
                        }}
                                                    placeholder={t('admin.products.familiesPlaceholder')}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs={12} className="my-2">
                    <Form.Label>{t('admin.products.departments')}</Form.Label>
                    <div className="mb-3">
                    {departmentOptions.map(dept => {
                        const departmentValue = dept.value

                        return (
                            <Form.Check
                                key={departmentValue}
                                type="checkbox"
                                inline
                                label={dept.label}
                                checked={selectedDepartments.includes(departmentValue)}
                                onChange={() => handleDepartmentChange(departmentValue)}
                            />
                        )
                    })}
                    </div>
                </Col>
            </Row>

            <Row>
                <Col xs={12} className="my-2">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5>{t('admin.products.attributes')}</h5>
                            {!state._id && (
                                <Button
                                    variant="success"
                                    size="xs"
                                    onClick={addAttribute}
                                    disabled={!isAdmin && hasDepartmentClaim && !editableDepartmentOptions.length}
                                >
                                    <i className='fas fa-plus me-2'></i> {t('global.add')} {t('admin.products.attribute')}
                                </Button>
                            )}
                        </Card.Header>
                        <Card.Body>
                        {attributes.map((attr) => (
                            <Row key={attr.id} className="mb-2">
                                <>
                                    <Col xs={11}>
                                        <Row>
                                            <Col xs={12} md={4}>
                                                {state._id
                                                    ? (
                                                        <Form.Control
                                                            plaintext
                                                            readOnly
                                                            value={attr.label || attr.key}
                                                        />
                                                    )
                                                    : (
                                                        <Form.Control
                                                            placeholder={t('admin.products.keyPlaceholder')}
                                                            value={attr.key}
                                                            onChange={(e) => {
                                                                setAttributes(prev =>
                                                                    prev.map(item =>
                                                                        item.id === attr.id
                                                                            ? { ...item, key: e.target.value }
                                                                            : item
                                                                    )
                                                                )
                                                            }}
                                                        />
                                                    )
                                                }
                                            </Col>

                                            <Col xs={12} md={7}>
                                                <Form.Control
                                                    placeholder={t('admin.products.valuePlaceholder')}
                                                    value={attr.value}
                                                    onChange={(e) => {
                                                        setAttributes(prev =>
                                                            prev.map(item =>
                                                                item.id === attr.id
                                                                    ? {
                                                                        ...item,
                                                                        value: e.target.value,
                                                                        label: item.label.replace(' (IA)', ''),
                                                                    }
                                                                    : item
                                                            )
                                                        )
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>

                                    {!state._id && (
                                        <Col xs={1} className="d-flex align-items-center justify-content-end">
                                            <i className="fas fa-trash cursor" onClick={() => removeAttribute(attr.id)}></i>
                                        </Col>
                                    )}
                                </>
                            </Row>
                        ))}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mt-4">
                <Col className="d-flex justify-content-end align-items-center">
                    <Button variant="secondary" onClick={onClose} className="me-2">
                        <i className="fas fa-times"></i> {t('global.cancel')}
                    </Button>
                    <Button variant="primary" type="submit" disabled={isRegistering}>
                        {isRegistering
                        ?
                            t('global.loading')
                        :
                        <>
                            <i className="fas fa-save me-2"></i> {t('global.save')}
                        </>
                        }
                    </Button>
                </Col>
            </Row>
        </Form>
    )
}
